import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { replaceStationTags } from "./adminApi";

type Props = {
  stationId: string;
  initialTags: string[];
};

export function AdminTagEditor({ stationId, initialTags }: Props) {
  const { t } = useTranslation();
  const [tags, setTags] = useState<string[]>(initialTags);
  const [input, setInput] = useState("");

  const mutation = useMutation({
    mutationFn: (next: string[]) => replaceStationTags(stationId, next),
    onSuccess: (data) => setTags(data.tags),
  });

  function addTag() {
    const trimmed = input.trim().toLowerCase();
    if (!trimmed || tags.includes(trimmed)) return;
    const next = [...tags, trimmed];
    setTags(next);
    setInput("");
    mutation.mutate(next);
  }

  function removeTag(tag: string) {
    const next = tags.filter((t) => t !== tag);
    setTags(next);
    mutation.mutate(next);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag();
    }
  }

  return (
    <section data-testid="admin-tag-editor" className="rounded-lg border border-ember/40 bg-ember/5 p-4">
      <h2 className="mb-2 text-sm font-semibold text-ember">
        {t("admin.tags_heading")}
      </h2>
      <div className="flex flex-wrap gap-1.5">
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full bg-ember/25 px-2.5 py-0.5 text-xs text-ember"
          >
            {tag}
            <button
              type="button"
              aria-label={t("admin.tag_remove", { tag })}
              onClick={() => removeTag(tag)}
              className="ml-0.5 text-ember hover:text-ember-deep"
            >
              x
            </button>
          </span>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("admin.tag_placeholder")}
          className="flex-1 rounded-md border border-ember/40 bg-surface px-2 py-1 text-sm text-fg placeholder:text-fg-faint focus:border-ember focus:outline-none focus:ring-1 focus:ring-volt"
        />
        <button
          type="button"
          onClick={addTag}
          disabled={mutation.isPending}
          className="rounded-md bg-ember-deep px-3 py-1 text-sm font-medium text-fg hover:bg-ember disabled:opacity-50"
        >
          {t("admin.tag_add")}
        </button>
      </div>
      {mutation.isError && (
        <p className="mt-1 text-xs text-ember">{t("admin.tags_save_failed")}</p>
      )}
    </section>
  );
}
